import { BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, useBCMSContext, useModel, useStateManager } from "@bcm-dev/bcms-vite";
import { PageContainer, PageContentView } from 'components/shared';
import { InfoBanner } from 'components/shared/views/ConfirmBannerView';
import CustomContentHeaderWidget from 'components/shared/views/CustomContentHeaderWidget';
import Classes, { Account, NativeAlert, NativeAlertHistory, UserAccount } from "model";
import React from 'react';
import AddEditAlertWidget from './components/AddEditAlertWidget';
import AlertHistoryWidget from './components/AlertHistoryWidget';
import CurrentAlertsWidget from './components/CurrentAlertsWidget';
import "./styles.css";
import "./mobile.css";


/**
 * @function AlertsPage
 * @return {React.Component}
 * @exported true
 * @description The main component for AlertsPage
 */
export default function AlertsPage(props) {
  const context = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, context);

  const data = { ...getDefaultPageData(), ...props.data ?? {} };
  const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };
  const className = props.className?(" "+props.className):"";

  const controller = (state, setState) => ({
      data: state.data,
      ui: state.ui,
      // START: Functionality for "addEditAlertWidget".
      onChange: prop => value => {
        BCMSStateManagerCRUD.init(state)
        .update([ "data", "addEditAlertWidget", "alert", prop ], value)
        .submit(setState);
      },

      onAccountChange: prop => value => {
        BCMSStateManagerCRUD.init(state)
        .update([ "data", "addEditAlertWidget", "alert", prop ], value.id)
        .submit(setState);
      },

      onChangePhoneEmail: (prop, id) => value => {
        let alert = state.data.addEditAlertWidget.alert;

        if(value === true) {
          if(prop === "phone") {
            if(!alert.userAccountIDsForPhone.includes(id)) {
              alert.userAccountIDsForPhone.push(id);
              alert.userAccountIDsForPhone.sort((a, b) => Number(a) - Number(b));
            }
          } else if(prop === "email") {
            if(!alert.userAccountIDsForEmail.includes(id)) {
              alert.userAccountIDsForEmail.push(id);
              alert.userAccountIDsForEmail.sort((a, b) => Number(a) - Number(b));
            }
          }
        } else {
          if(prop === "phone") {
            if(alert.userAccountIDsForPhone.includes(id)) {
              alert.userAccountIDsForPhone = alert.userAccountIDsForPhone.filter(item => item !== id);
            }
          } else if(prop === "email") {
            if(alert.userAccountIDsForEmail.includes(id)) {
              alert.userAccountIDsForEmail = alert.userAccountIDsForEmail.filter(item => item !== id);
            }
          }
        }

        BCMSStateManagerCRUD.init(state)
        .update([ "data", "addEditAlertWidget", "alert", "userAccountIDsForPhone" ], alert.userAccountIDsForPhone)
        .update([ "data", "addEditAlertWidget", "alert", "userAccountIDsForEmail" ], alert.userAccountIDsForEmail)
        .submit(setState);
      },

      onAddSubscriptionClick: value => {
        BCMSStateManagerCRUD.init(state)
        .update([ "ui", "addEditAlertWidget", "form", "isActive" ], state.data.addEditAlertWidget.alert.type === "balanceAlert")
        .update([ "ui", "addEditAlertWidget", "alertCreatedSuccessfullyBanner", "isActive" ], false)
        .submit(setState);
      },

      onCancelClick: () => {
        BCMSStateManagerCRUD.init(state)
        .update([ "data", "addEditAlertWidget" ], getDefaultPageData().addEditAlertWidget)
        .update([ "ui", "addEditAlertWidget", "form", "isActive" ], false)
        .submit(setState);
      },

      onSubmitClick: () => {
        const alert = state.data.addEditAlertWidget.alert;
        const defaultAlert = BCMSDefaultModel.nativeAlert();
        const mergeAlerts = BCMSDefaultModel.mergeRecords(alert.record, defaultAlert.record);
 
        const newAlert = NativeAlert.init({ 
          ...mergeAlerts, 
          id: NativeAlert.nextID(), 
          userAccountIDsForEmail: alert.userAccountIDsForEmail,
          userAccountIDsForPhone: alert.userAccountIDsForPhone
        });

        // Update the state.
        BCMSStateManagerCRUD.init(state)
        .update([ "data", "addEditAlertWidget" ], getDefaultPageData().addEditAlertWidget)
        .update([ "data", "currentAlertsWidget", "alerts" ], [ newAlert, ...getDefaultPageData().currentAlertsWidget.alerts ])
        .update([ "ui", "addEditAlertWidget", "form", "isActive" ], false)
        .update([ "ui", "addEditAlertWidget", "alertCreatedSuccessfullyBanner", "isActive" ], true)
        .submit(setState);

        // Update the alerts globally.
        BCMSRecord.batch([
            NativeAlert.create(newAlert)
        ], context, Classes);
      },
      // END: Functionality for "addEditAlertWidget".

      onInfoBannerClose: () => {
        BCMSStateManagerCRUD.init(state)
        .update([ "ui", "addEditAlertWidget", "alertCreatedSuccessfullyBanner", "isActive" ], false)
        .submit(setState);
      }
  });

  return (
    <PageContainer className={"alerts-page"+className} state={ { data, ui } } controller={ controller }>
      <PageContent/>
    </PageContainer>
  );
}

export const getDefaultPageData = () => {
  return { 
    addEditAlertWidget: {
      alert: NativeAlert.init({ 
        type: "prompt",
        account: Account.initAll()[0].id,
        userAccountIDsForPhone: UserAccount.initAll().reduce((acc, item) => {
            acc.push(item.id);
            return acc;
        }, []),
        userAccountIDsForEmail: UserAccount.initAll().reduce((acc, item) => {
            acc.push(item.id);
            return acc;
        }, []),
       }),
      allUserAccountsIDsForPhone: UserAccount.initAll().reduce((acc, item) => {
          acc.push(item.id);
          return acc;
      }, []),
      allUserAccountsIDsForEmail: UserAccount.initAll().reduce((acc, item) => {
        acc.push(item.id);
        return acc;
    }, [])
    },
    currentAlertsWidget: {
      alerts: NativeAlert.initAll().filter(item => item.id !== undefined).sort((a, b) => - Number(b.id) - Number(a.id))
    },
    alertHistoryWidget: {
      alertHistory: NativeAlertHistory.initAll()
    }
  }
};

export const getDefaultPageUI = () => ({
  addEditAlertWidget: {
    form: { isActive: false },
    formAccountSelect: { isActive: false },
    alertTypeSelect: { isActive: false },
    alertCreatedSuccessfullyBanner: { isActive: false }
  },
  currentAlertsWidget: {},
  alertHistoryWidget: {}
});

function PageContent() {
  const { ui, onInfoBannerClose } = useStateManager();
  const { branding } = useModel();
  const { options } = branding;
  const promoText = options.nativealerts_promo_abi22;
  const subfooterText = options.nativealerts_subfooter_abi22;

  return (
    <PageContentView>
    { promoText && <CustomContentHeaderWidget content= { promoText } />}
    { ui.addEditAlertWidget.alertCreatedSuccessfullyBanner.isActive && <InfoBanner dataPoint="Alerts_Page_Info_Banner" text="Your alert was created successfully." icon="" onClick={ onInfoBannerClose } /> }

    <AddEditAlertWidget/>
    <CurrentAlertsWidget />
    <AlertHistoryWidget />
    { subfooterText && <CustomContentHeaderWidget content={ subfooterText } /> }
  </PageContentView>
  )
}